import { useCallback, useEffect, useMemo, useState } from 'react';
import { pmaImportAPI } from '../services/api';
import { readPmaApiError } from '../domain/pma/pmaApiError';
import {
  DEFAULT_RESERVED_UNIX_FORM,
  EMPTY_RESERVED_UNIX_TOUCHED,
  applyReservedUnixFormChange,
  formFromPreset,
  reservedUnixPlanPayload,
  reservedUnixPlanSummary,
} from '../domain/identity/reservedUnixStudentForm';

export default function useReservedUnixStudents() {
  const [form, setForm] = useState({ ...DEFAULT_RESERVED_UNIX_FORM });
  const [touched, setTouched] = useState({ ...EMPTY_RESERVED_UNIX_TOUCHED });
  const [presetId, setPresetId] = useState('');
  const [presets, setPresets] = useState([]);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    pmaImportAPI
      .listReservedUnixPresets()
      .then((res) => {
        if (!cancelled) setPresets(Array.isArray(res.data?.presets) ? res.data.presets : []);
      })
      .catch(() => {
        if (!cancelled) setPresets([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const summary = useMemo(() => reservedUnixPlanSummary(form, touched), [form, touched]);

  const setField = useCallback((field, value) => {
    setForm((current) => {
      const next = applyReservedUnixFormChange(current, field, value, touched);
      setTouched(next.touched);
      return next.form;
    });
    setPreview(null);
    setResult(null);
    setError('');
  }, [touched]);

  const applyPreset = useCallback((id) => {
    setPresetId(id);
    setError('');
    setPreview(null);
    setResult(null);
    if (!id) {
      setForm({ ...DEFAULT_RESERVED_UNIX_FORM });
      setTouched({ ...EMPTY_RESERVED_UNIX_TOUCHED });
      return;
    }
    const preset = presets.find((item) => item.id === id);
    setForm(formFromPreset(preset));
    setTouched({ slug: true, prefix: true, emailDomain: true });
  }, [presets]);

  const payload = useCallback(
    () => reservedUnixPlanPayload(form, touched),
    [form, touched]
  );

  const previewPlan = useCallback(async () => {
    setError('');
    setResult(null);
    setPreviewing(true);
    try {
      const res = await pmaImportAPI.previewReservedUnix(payload());
      setPreview(res.data);
    } catch (err) {
      setPreview(null);
      setError(await readPmaApiError(err, 'Could not preview reserved unix students'));
    } finally {
      setPreviewing(false);
    }
  }, [payload]);

  const importPlan = useCallback(async () => {
    const label = summary.org || 'this group';
    const range = summary.firstUnix
      ? ` (${summary.firstUnix}${summary.count > 1 ? ` … ${summary.lastUnix}` : ''})`
      : '';
    const confirmed = window.confirm(
      `Create ${summary.count || 0} SMS student${summary.count === 1 ? '' : 's'} for ${label}${range}?\n\nLinux / VNC accounts will not be created.`
    );
    if (!confirmed) return;

    setError('');
    setImporting(true);
    try {
      const res = await pmaImportAPI.importReservedUnix(payload());
      setResult(res.data);
      setPreview(res.data);
    } catch (err) {
      setResult(null);
      setError(await readPmaApiError(err, 'Could not create reserved unix students'));
    } finally {
      setImporting(false);
    }
  }, [payload, summary]);

  return {
    form,
    setField,
    presetId,
    applyPreset,
    presets,
    summary,
    preview,
    result,
    error,
    previewing,
    importing,
    previewPlan,
    importPlan,
  };
}
